import { useEffect, useRef } from 'react';

// El botón atrás del móvil cierra la "capa" abierta (correo, editor, hoja) en
// vez de salir de la página.
//
// Mecánica:
// - Al abrirse la capa se empuja una entrada en el historial con la misma URL
//   y en su estado la pila de capas abiertas (`duettoCapas`, la última arriba).
// - Al pulsar atrás (popstate) cada capa mira si su entrada sigue en la pila:
//   si ya no está, es que el atrás era para ella y llama a `onAtras`.
// - Si `onAtras` no llega a cerrarla (el editor pregunta "¿Guardar borrador?"),
//   se vuelve a empujar la entrada para que el siguiente atrás también funcione.
// - Si la capa se cierra desde la pantalla (X, Enviar, Archivar...) y su entrada
//   está arriba, se hace history.back() para no dejarla colgando. Si arriba hay
//   otra cosa (se navegó a otra ruta con navigate), no se toca el historial.
// - Mientras un back() propio está en curso, los empujes y quitas que pidan otras
//   capas esperan a que termine (back() es asíncrono y pushState no).

const hayHistorial = () => typeof window !== 'undefined' && !!window.history?.pushState;

const pilaActual = () => {
  const pila = window.history.state?.duettoCapas;
  return Array.isArray(pila) ? pila : [];
};

const estaEnPila = (clave) => pilaActual().includes(clave);

let atrasEnCurso = false;
const cola = []; // [{ tipo: 'empujar' | 'quitar', clave }]

const colaTiene = (tipo, clave) => cola.some((op) => op.tipo === tipo && op.clave === clave);

function empujar(clave) {
  if (atrasEnCurso) {
    cola.push({ tipo: 'empujar', clave });
    return;
  }
  const estado = window.history.state || {};
  // Se conserva el estado de React Router (key, idx, usr) para que no vea otra ruta
  window.history.pushState({ ...estado, duettoCapa: clave, duettoCapas: [...pilaActual(), clave] }, '');
}

function vaciarCola() {
  while (cola.length && !atrasEnCurso) {
    const op = cola.shift();
    // Se decide con el historial ya asentado: solo se empuja si la entrada no está
    if (op.tipo === 'empujar') {
      if (!estaEnPila(op.clave)) empujar(op.clave);
    } else {
      quitar(op.clave);
    }
  }
}

function quitar(clave) {
  if (atrasEnCurso) {
    cola.push({ tipo: 'quitar', clave });
    return;
  }
  if (window.history.state?.duettoCapa !== clave) return;
  atrasEnCurso = true;
  let hecho = false;
  let seguro = null;
  const alTerminar = () => {
    if (hecho) return;
    hecho = true;
    window.removeEventListener('popstate', alTerminar);
    clearTimeout(seguro);
    atrasEnCurso = false;
    vaciarCola();
  };
  window.addEventListener('popstate', alTerminar);
  // Por si el navegador no llega a avisar del popstate
  seguro = setTimeout(alTerminar, 1500);
  window.history.back();
}

// clave: texto único de la capa ('correo', 'editor', 'hoja-mas').
// activa: true mientras la capa está abierta.
// onAtras: función que cierra la capa (se llama cuando el usuario pulsa atrás).
export default function useCapaAtras(clave, activa, onAtras) {
  const onAtrasRef = useRef(onAtras);
  onAtrasRef.current = onAtras;
  const activaRef = useRef(activa);
  activaRef.current = activa;
  const porAtrasRef = useRef(false);

  useEffect(() => {
    if (!activa || !hayHistorial()) return undefined;
    porAtrasRef.current = false;
    // Con un back() propio en curso el estado actual aún es el viejo: se encola
    if ((atrasEnCurso || !estaEnPila(clave)) && !colaTiene('empujar', clave)) empujar(clave);

    const alVolver = () => {
      // Nuestra entrada sigue (o está por empujar): el atrás era de otra capa
      if (estaEnPila(clave) || colaTiene('empujar', clave)) return;
      porAtrasRef.current = true;
      if (onAtrasRef.current) onAtrasRef.current();
      setTimeout(() => {
        // La capa sigue abierta: se repone la entrada para el siguiente atrás
        if (activaRef.current && !estaEnPila(clave) && !colaTiene('empujar', clave)) {
          porAtrasRef.current = false;
          empujar(clave);
        }
      }, 0);
    };

    window.addEventListener('popstate', alVolver);
    return () => {
      window.removeEventListener('popstate', alVolver);
      // Cerrada desde la pantalla (no con atrás): se quita su entrada si está arriba
      if (!porAtrasRef.current) quitar(clave);
      porAtrasRef.current = false;
    };
  }, [activa, clave]);
}
